import React, { useEffect, useMemo, useRef, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import CustomForm from "../../../custom/Forms/CustomForm.js";
import SelectInput from "../../../custom/components/Select-Input/SelectInput.js";
import TextAreaInput from "../../../custom/components/TextArea-Input/TextAreaInput.js";
import CheckboxInput from "../../../custom/components/CheckBox-Input/CheckboxInput.js";
import DatePicker from "../../../custom/components/Date-Picker/DatePicker.js";
import { useNavigate, useParams } from "react-router-dom";
import { createPageData, basicFormData } from "./ItemIntakeFile.js";
import { validate } from "../../../custom/validations/Validate.js";
import {
  formatDate,
  getRecords,
  saveRecords,
} from "../../../services/api.service.js";
import { toast } from "react-toastify";
import Breadcrumbs from "../../../components/Common/Breadcrumb.js";
import Asterisks from "../../../custom/components/Asterisks/Asterisks.js";
import CustomError from "../../../custom/components/Errors/CustomError.js";
import { LoaderCreatePage } from "../../Loader/Loader.js";
import { loaderOverlay } from "../../../Style/loaderOverly.js";
import TextInput from "../../../custom/components/Text-Input/TextInput.js";
import NumberInput from "../../../custom/components/Number-Input/NumberInput.js";

const ItemIntakeCreate = () => {
  const navigate = useNavigate();

  const {
    payload,
    navigatePage,
    headerName,
    validationType,
    createEndpoint,
    updateEndpoint,
  } = createPageData();
  const schema = localStorage.getItem("schema");
  const initialPayload = useMemo(() => payload, []);

  const [loadingData, setLoadingData] = useState(false);
  const [formData, setFormData] = useState(basicFormData());
  const [formErrors, setFormErrors] = useState({});

  const [pageMode, setPageMode] = useState(null);
  const [existingItem, setExistingItem] = useState(null);

  const [materialOptions, setMaterialOptions] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  // read-only, auto-fetched from the selected GMDS Code's material master
  const [itemDescription, setItemDescription] = useState("");

  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  const [technicianDetails, setTechnicianDetails] = useState(null);
  const [scrapReasonOptions, setScrapReasonOptions] = useState([]);
  const [serialSuggestions, setSerialSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const { id } = useParams();

  // ---------- helpers ----------
  const showFullForm = pageMode === "NEW" || pageMode === "EXISTING_DISPATCHED";
  const showSubmitButton = showFullForm;
  const serialSearchRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        serialSearchRef.current &&
        !serialSearchRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    (async () => {
      const mats = await getRecords(
        {
          modelName: "material_masters",
          schema,
          whereCondition: { is_active: true },
          orderBy: {
            field: "name",
            order: "asc",
          },
          include: [
            {
              model: "category_masters",
              as: "category",
              where: {
                type: 0,
              },
              required: true,
            },
          ],
        },

        "/getRecords",
      );
      setMaterialOptions(
        (mats?.data || []).map((m) => ({
          value: m.id,
          label: m.gmds_code || m.name,
          // carried on the option so selecting a GMDS Code can auto-fill Item description
          descriptions: m.descriptions,
        })),
      );
    })();
    getScrapReasonData();
    getBranchData();
  }, [schema]);

  const getBranchData = async () => {
    try {
      const res = await getRecords(
        {
          modelName: "branch_masters",
          schema,
          whereCondition: { is_active: true },
          orderBy: { field: "branch_name", order: "asc" },
          include: [{ model: "zone_masters", as: "zone" }],
        },
        "/getRecords",
      );
      setBranchOptions(
        (res?.data || []).map((b) => ({
          value: b.id,
          label: b.branch_name,
          // carried on the option so selecting a Branch can auto-fill Zone
          zone_id: b.zone_id,
          zone_name: b.zone?.zone_name,
        })),
      );
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };
  const getScrapReasonData = async () => {
    try {
      const payload = {
        modelName: "scrap_reason_masters",
        whereCondition: {
          is_active: true,
        },
        schema: schema,
        orderBy: {
          field: "reason",
          order: "asc",
        },
      };

      const responseData = await getRecords(payload, "/getRecords");

      let newArray = [
        ...(responseData.data?.map((item) => ({
          value: item.id,
          label: item.reason,
        })) || []),
        { value: 0, label: "Other" },
      ];

      setScrapReasonOptions(newArray);
    } catch (error) {
      console.error("Error fetching scrap reasons:", error);
    }
  };

  const handleSerialGo = async () => {
    setSerialSuggestions([]);
    setShowSuggestions(false);
    const srNo = formData?.inputData?.material_serial_no;
    if (!srNo) {
      setFormErrors((prev) => ({
        ...prev,
        material_serial_no: "Serial number cannot be blank",
      }));
      return;
    }
    setLoadingData(true);
    try {
      const res = await getRecords(
        {
          modelName: "material_intakes",
          schema,
          whereCondition: { material_serial_no: srNo },
          include: [
            {
              model: "material_masters",
              as: "material",
            },

            {
              model: "client_masters",
              as: "customer",
            },
            {
              model: "user_masters",
              as: "received_by_user",
            },
            {
              model: "user_masters",
              as: "inspected_by_user",
            },
            {
              model: "job_cards",
              as: "job_cards",
              required: false,
              include: [
                {
                  model: "user_masters",
                  as: "technician",
                },
              ],
            },
          ],
          // search: {
          //   searchKeyword: srNo,
          //   field_name: ["material_serial_no"],
          // },
        },
        "/getRecords",
      );

      if (!res?.data || res.data.length === 0) {
        setExistingItem(null);
        setPageMode("NEW");
        setTechnicianDetails(null);
        return;
      }

      const item = res.data[0];
      setExistingItem(item);

      setFormData((prev) => ({
        ...prev,
        inputData: {
          ...prev.inputData,
          material_id: item.material_id,
        },
      }));

      setSelectedMaterial({
        value: item.material_id,
        label: item.material?.gmds_code || item.material?.name,
      });
      setItemDescription(item.material?.descriptions || "");

      if (item.status === "Pending") {
        setPageMode("EXISTING_PENDING");
      } else if (item.status === "Dispatched") {
        setPageMode("EXISTING_DISPATCHED");
      } else {
        setPageMode(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingData(false);
    }
  };

  const fetchTechnician = async () => {
    try {
      setLoadingData(true);
      const formFields = formData?.inputData;

      let payload = {
        is_repairable: formFields?.is_repairable,
        material_id: formFields?.material_id,
        branch_id: formFields?.branch_id,
        zone_id: formFields?.zone_id,
      };

      const responseData = await saveRecords(payload, "/assign-technician");

      if (responseData && responseData.code === 1) {
        setTechnicianDetails(responseData?.data?.assign_technician);
      } else {
        toast.error(responseData?.message || "Failed to fetch engineer");
        setTechnicianDetails(null);

        setFormData((p) => ({
          ...p,
          inputData: {
            ...p.inputData,

            is_repairable: null,
          },
        }));
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong while fetching engineer");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (formData?.inputData?.is_repairable === true && showFullForm) {
      fetchTechnician();
    } else {
      setTechnicianDetails(null);
    }
  }, [formData?.inputData?.is_repairable, showFullForm]);

  const onInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormErrors((prev) => ({
      ...prev,
      [name]: validate(validationType, { [name]: value })?.[name] || null,
    }));
    setFormData((prev) => ({
      ...prev,
      inputData: {
        ...prev.inputData,
        [name]: type === "checkbox" ? !!checked : value,
      },
    }));
  };
  const handleMaterialSelect = (options) => {
    const selectedValue = options ? options.value : "";
    const fieldError = validate(validationType, {
      material_id: selectedValue,
    });

    setSelectedMaterial(options);
    setItemDescription(options ? options.descriptions || "" : "");

    setFormData((prev) => ({
      ...prev,
      inputData: {
        ...prev.inputData,
        material_id: selectedValue,
        received_date: "",

        fault_reported_from_field: "",
        comments: "",
        rate_per_unit: "",
        items_matching_invoice: null,
        anti_static_bag: null,
        fault_tag_attached: null,
        is_repairable: null,
        scrap_reason_id: null,
        scrap_remark: "",
      },
    }));

    // clear technician
    setTechnicianDetails(null);

    // clear zones

    // clear errors
    setFormErrors((prev) => ({
      ...prev,
      material_id: null,
      received_date: null,

      fault_reported_from_field: null,
      rate_per_unit: null,
      items_matching_invoice: null,
      anti_static_bag: null,
      fault_tag_attached: null,
      is_repairable: null,
      scrap_reason_id: null,
      scrap_remark: null,
    }));
  };

  const handleBranchSelect = (options) => {
    const selectedValue = options ? options.value : "";
    const fieldError = validate(validationType, { branch_id: selectedValue });

    setSelectedBranch(options);
    setFormErrors((prev) => ({
      ...prev,
      branch_id: fieldError?.branch_id || "",
    }));

    setFormData((prev) => ({
      ...prev,
      inputData: {
        ...prev.inputData,
        branch_id: selectedValue,
        zone_id: options ? options.zone_id : "",
      },
    }));
  };

  const handleSerialSearch = async (value) => {
    if (!value || value.length < 2) {
      setSerialSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const res = await getRecords(
        {
          modelName: "material_intakes",
          schema,
          search: {
            searchKeyword: value,
            field_name: ["material_serial_no"],
          },
          include: [{ model: "material_masters", as: "material" }],
        },
        "/getRecords",
      );

      if (res?.data?.length > 0) {
        setSerialSuggestions(res.data);
        setShowSuggestions(true);
      } else {
        setSerialSuggestions([]);
        setShowSuggestions(true);
      }
    } catch (err) {
      console.error(err);
      setSerialSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSubmit = async (e) => {
    if (formData?.inputData?.is_repairable === true && !technicianDetails) {
      toast.error(
        "Engineer is required. Please select another item for appropriate engineer.",
      );

      setFormData((p) => ({
        ...p,
        inputData: {
          ...p.inputData,

          is_repairable: null,
        },
      }));
      return;
    }

    try {
      e.preventDefault();
      setLoadingData(true);

      const userDetails = JSON.parse(localStorage.getItem("authUser"));
      const validationErrors = validate(validationType, formData.inputData);

      setFormErrors(validationErrors);
      if (formData?.inputData?.is_repairable === true) {
        delete validationErrors?.scrap_reason_id;
        delete validationErrors?.scrap_remark;
      }
      if (formData?.inputData?.is_repairable === false) {
        delete validationErrors?.is_repairable;
      }
      if (formData?.inputData?.items_matching_invoice === false) {
        delete validationErrors?.items_matching_invoice;
      }
      if (formData?.inputData?.anti_static_bag === false) {
        delete validationErrors?.anti_static_bag;
      }
      if (formData?.inputData?.fault_tag_attached === false) {
        delete validationErrors?.fault_tag_attached;
      }

      if (formData?.inputData?.scrap_reason_id === 0) {
        delete validationErrors?.scrap_reason_id;
      }

      if (
        formData?.inputData?.is_repairable === false &&
        formData?.inputData?.scrap_reason_id &&
        formData?.inputData?.scrap_reason_id !== 0
      ) {
        delete validationErrors.scrap_remark;
      }

      if (JSON.stringify(validationErrors) !== "{}") return;

      const formFields = formData?.inputData;
      let payload =
        formFields.is_repairable === true
          ? {
              modelName: initialPayload?.modelName,
              schema: initialPayload?.schema,
              inputData: {
                intake_no: "",
                material_id: formFields?.material_id,
                incoming_invoice_no: formFields?.incoming_invoice_no,
                received_date: formFields?.received_date,
                received_by: userDetails?.id,
                is_active: formFields?.is_active,
                material_serial_no: formFields?.material_serial_no,
                items_matching_invoice: formFields?.items_matching_invoice,
                comments: formFields?.comments,
                anti_static_bag: formFields?.anti_static_bag,
                fault_tag_attached: formFields?.fault_tag_attached,
                fault_reported_from_field:
                  formFields?.fault_reported_from_field,
                branch_id: formFields?.branch_id,
                zone_id: formFields?.zone_id,
                rate_per_unit: formFields?.rate_per_unit,
                is_repairable: formFields?.is_repairable,
                technician_id: technicianDetails?.technician_id,
              },
            }
          : {
              modelName: initialPayload?.modelName,
              schema: initialPayload?.schema,
              inputData: {
                intake_no: "",
                material_id: formFields?.material_id,
                incoming_invoice_no: formFields?.incoming_invoice_no,
                received_date: formFields?.received_date,
                received_by: userDetails?.id,
                is_active: formFields?.is_active,
                material_serial_no: formFields?.material_serial_no,
                items_matching_invoice: formFields?.items_matching_invoice,
                comments: formFields?.comments,
                anti_static_bag: formFields?.anti_static_bag,
                fault_tag_attached: formFields?.fault_tag_attached,
                fault_reported_from_field:
                  formFields?.fault_reported_from_field,
                branch_id: formFields?.branch_id,
                zone_id: formFields?.zone_id,
                rate_per_unit: formFields?.rate_per_unit,
                scrap_reason_id: formFields.scrap_reason_id,
                remark: formFields.scrap_remark,
                is_repairable: formFields?.is_repairable,
              },
            };

      // console.log("payload", payload);
      // return;

      if (id) {
        // update
        payload = {
          ...payload,
          id: id,
          inputData: {
            ...payload?.inputData,
            created_by: userDetails?.id,
          },
        };
      } else {
        payload = {
          ...payload,
          inputData: {
            ...payload?.inputData,
            created_by: userDetails?.id,
            updated_by: userDetails?.id,
          },
        };
      }
      // console.log("payload", payload);
      // return;
      const responseData = id
        ? await saveRecords(payload, updateEndpoint)
        : await saveRecords(payload, createEndpoint);

      if (responseData && responseData.code === 1) {
        toast.success(
          `${headerName} ${id ? "updated" : "saved"} successfully!`,
        );
        navigate(navigatePage?.back);
      } else {
        toast.error(
          responseData.message || `Error while saving ${headerName}!`,
        );
      }
    } catch (error) {
      console.error("Error during submission:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setLoadingData(false);
    }
  };

  const isRepairableDisabled = !selectedMaterial?.value;

  return (
    <div className="page-content">
      <Breadcrumbs title={"Intake Item"} breadcrumbItems={[]} />
      <div className="card mb-3">
        <div className="container-fluid p-4 ">
          <Row className="">
            <Col md="6" className="m-3">
              <label className="form-label">
                Serial Number <Asterisks asterisk={true} />
              </label>

              <div className="d-flex align-items-start gap-2">
                <div
                  className="flex-grow-1 position-relative"
                  ref={serialSearchRef}
                >
                  <input
                    type="search"
                    className={`form-control ps-5 ${
                      formErrors?.material_serial_no && "is-invalid"
                    }`}
                    name="material_serial_no"
                    value={formData?.inputData?.material_serial_no || ""}
                    autoComplete="off"
                    onChange={(e) => {
                      onInputChange(e);
                      setExistingItem(null);
                      setPageMode(null);
                      setFormData((prev) => ({
                        ...prev,
                        inputData: {
                          ...prev.inputData,
                          material_serial_no: e.target.value,
                          received_date: "",

                          fault_reported_from_field: "",
                          comments: "",
                          rate_per_unit: "",
                          items_matching_invoice: null,
                          anti_static_bag: null,
                          fault_tag_attached: null,
                          is_repairable: null,
                          scrap_reason_id: null,
                          scrap_remark: "",
                        },
                      }));
                      setSelectedMaterial(null);
                      setItemDescription("");

                      setFormErrors((prev) => ({
                        ...prev,
                        material_id: null,
                        received_date: null,

                        fault_reported_from_field: null,
                        rate_per_unit: null,
                        items_matching_invoice: null,
                        anti_static_bag: null,
                        fault_tag_attached: null,
                        is_repairable: null,
                        scrap_reason_id: null,
                        scrap_remark: null,
                      }));
                      handleSerialSearch(e.target.value);
                    }}
                    placeholder="Enter Serial Number"
                  />
                  <span
                    className="position-absolute text-muted"
                    style={{
                      left: "14px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                    }}
                  >
                    🔍
                  </span>
                  {formErrors?.material_serial_no && (
                    <CustomError error={formErrors?.material_serial_no} />
                  )}

                  {showSuggestions &&
                    (serialSuggestions.length > 0 ? (
                      <div
                        className="position-absolute start-0 w-100 bg-white border rounded shadow-sm mt-1"
                        style={{
                          zIndex: 1000,
                          maxHeight: "300px",
                          overflowY: "auto",
                        }}
                      >
                        {serialSuggestions.map((item) => (
                          <div
                            key={item.id}
                            className="px-3 py-2 d-flex justify-content-between align-items-center suggestion-item"
                            style={{ cursor: "pointer" }}
                            onMouseDown={() => {
                              setFormData((prev) => ({
                                ...prev,
                                inputData: {
                                  ...prev.inputData,
                                  material_serial_no: item.material_serial_no,
                                },
                              }));

                              setSerialSuggestions([]);
                              setShowSuggestions(false);
                            }}
                          >
                            <div>
                              <strong>{item.material_serial_no}</strong>
                              <div className="text-muted small">
                                {item.material?.gmds_code || item.material?.name}
                              </div>
                            </div>
                            <span className="badge bg-light text-dark">
                              {item.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div
                        className="position-absolute start-0 w-100 bg-white border rounded shadow-sm mt-1"
                        style={{
                          zIndex: 1000,
                          padding: "10px",
                          textAlign: "center",
                        }}
                      >
                        No data found..
                      </div>
                    ))}
                </div>

                {/* GO BUTTON */}
                <button
                  type="submit"
                  onClick={handleSerialGo}
                  className="btn btn-primary d-flex align-items-center justify-content-center"
                  style={{
                    height: "38px",
                    minWidth: "80px",
                    fontSize: "13px",
                  }}
                >
                  GO
                </button>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {existingItem && (
        <div className="card mb-3">
          <div className="container-fluid p-4 ">
            <Row className="">
              <Col md="12">
                <h5>Item Details</h5>
              </Col>
              <Col md="4" className="mt-1">
                <strong>GMDS Code:</strong>{" "}
                {existingItem?.material?.gmds_code ||
                  existingItem?.material?.name ||
                  "-"}
              </Col>
              <Col md="4" className="mt-2">
                <strong>Item Description:</strong>{" "}
                {existingItem?.material?.descriptions || "-"}
              </Col>
              <Col md="4" className="mt-2">
                <strong>Serial No:</strong> {existingItem?.material_serial_no}
              </Col>
              <Col md="4" className="mt-2">
                <strong>Received Date:</strong>{" "}
                {formatDate(existingItem?.received_date) || "-"}
              </Col>
              <Col md="4" className="mt-2">
                <strong>Status:</strong> {existingItem.status}
              </Col>

              <Col md="4" className="mt-2">
                <strong>Items Matching Invoice: </strong>
                {existingItem?.items_matching_invoice == null
                  ? "-"
                  : existingItem?.items_matching_invoice
                    ? "Yes"
                    : "No"}{" "}
              </Col>

              <Col md="4" className="mt-2">
                <strong>Anti Static Bag: </strong>
                {existingItem?.anti_static_bag == null
                  ? "-"
                  : existingItem?.anti_static_bag
                    ? "Yes"
                    : "No"}{" "}
              </Col>
              <Col md="4" className="mt-2">
                <strong>Fault Tag Attached: </strong>
                {existingItem?.fault_tag_attached == null
                  ? "-"
                  : existingItem?.fault_tag_attached
                    ? "Yes"
                    : "No"}{" "}
              </Col>
              <Col md="4" className="mt-2">
                <strong>Fault Reported From Field: </strong>
                {existingItem?.fault_reported_from_field || "-"}
              </Col>

              <Col md="4" className="mt-2">
                <strong>Rate Per Unit: </strong>
                {existingItem?.rate_per_unit || "-"}
              </Col>
              <Col md="4" className="mt-2">
                <strong>Received By: </strong>
                {existingItem?.received_by_user?.first_name || "-"}{" "}
                {existingItem?.received_by_user?.last_name || "-"}
              </Col>
            </Row>
          </div>
        </div>
      )}

      {showFullForm && (
        <div className="card mb-3">
          <div className="container-fluid p-4 ">
            <Row className="">
              <Col md="6" className="mt-3">
                <SelectInput
                  label="GMDS Code"
                  options={materialOptions}
                  value={selectedMaterial}
                  onSelect={handleMaterialSelect}
                  placeholder="Select GMDS Code"
                  inputClassName="form-control"
                  labelClassName="form-label"
                  asterisk={true}
                  customError={
                    formErrors?.material_id && "GMDS Code cannot be blank."
                  }
                />
              </Col>

              <Col md="6" className="mt-3">
                <TextInput
                  label="Item Description"
                  name="item_description"
                  value={itemDescription}
                  onChange={() => {}}
                  placeholder="-"
                  inputClassName="form-control"
                  labelClassName="form-label"
                  readOnly={true}
                />
              </Col>

              <Col md="6" className="mt-3">
                <TextInput
                  label="Incoming Invoice No."
                  name="incoming_invoice_no"
                  value={formData?.inputData?.incoming_invoice_no || ""}
                  onChange={onInputChange}
                  placeholder="Enter Incoming Invoice No."
                  inputClassName="form-control"
                  labelClassName="form-label"
                  asterisk={true}
                  customError={formErrors?.incoming_invoice_no}
                />
              </Col>

              <Col md="6" className="mt-3">
                <DatePicker
                  label="Received Date"
                  name="received_date"
                  value={formData?.inputData?.received_date}
                  onChange={onInputChange}
                  placeholder="Received Date"
                  inputClassName="form-control"
                  labelClassName="form-label"
                  asterisk={true}
                  customError={formErrors?.received_date}
                />
              </Col>

              <Col md="6" className="mt-3">
                <SelectInput
                  label="Branch"
                  options={branchOptions}
                  value={selectedBranch}
                  onSelect={handleBranchSelect}
                  placeholder="Select Branch"
                  inputClassName="form-control"
                  labelClassName="form-label"
                  asterisk={true}
                  customError={formErrors?.branch_id}
                />
              </Col>

              <Col md="6" className="mt-3">
                <TextInput
                  label="Zone"
                  name="zone"
                  value={selectedBranch?.zone_name || ""}
                  onChange={() => {}}
                  placeholder="-"
                  inputClassName="form-control"
                  labelClassName="form-label"
                  readOnly={true}
                />
              </Col>

              <Col md="6" className="mt-3">
                <TextAreaInput
                  label="Fault Reported from Field"
                  name="fault_reported_from_field"
                  value={formData.inputData.fault_reported_from_field}
                  onChange={onInputChange}
                  placeholder="Enter fault details"
                  inputClassName="form-control"
                  labelClassName="form-label"
                  asterisk={true}
                  customError={formErrors?.fault_reported_from_field}
                />
              </Col>
              {/* Comments */}
              <Col md="6" className="mt-3">
                <TextAreaInput
                  label="Comments"
                  name="comments"
                  value={formData.inputData.comments}
                  onChange={onInputChange}
                  placeholder="Enter comments"
                  inputClassName="form-control"
                  labelClassName="form-label"
                  // asterisk={true}
                />
              </Col>
              {/* Rate */}
              <Col md="6" className="mt-3">
                <NumberInput
                  label="Rate Per Unit"
                  name="rate_per_unit"
                  value={formData.inputData.rate_per_unit}
                  onChange={onInputChange}
                  placeholder="Enter Rate Per Unit"
                  inputClassName="form-control"
                  labelClassName="form-label"
                  asterisk={true}
                  customError={formErrors?.rate_per_unit}
                />
              </Col>

              <Row className="mt-0">
              {/* Items Matching with Invoice */}
              <Col md="6" className="mt-4">
                {/* Field row */}
                <div className="d-flex align-items-center gap-3">
                  <label className="form-label mb-0">
                    Items Matching with Invoice? <Asterisks asterisk={true} /> :
                  </label>

                  <div className="d-flex gap-3">
                    <label>
                      <input
                        type="radio"
                        checked={
                          formData.inputData.items_matching_invoice === true
                        }
                        onChange={() => {
                          setFormData((p) => ({
                            ...p,
                            inputData: {
                              ...p.inputData,
                              items_matching_invoice: true,
                            },
                          }));
                          setFormErrors((prev) => ({
                            ...prev,
                            items_matching_invoice: null,
                          }));
                        }}
                      />{" "}
                      Yes
                    </label>

                    <label>
                      <input
                        type="radio"
                        checked={
                          formData.inputData.items_matching_invoice === false
                        }
                        onChange={() => {
                          setFormData((p) => ({
                            ...p,
                            inputData: {
                              ...p.inputData,
                              items_matching_invoice: false,
                            },
                          }));
                          setFormErrors((prev) => ({
                            ...prev,
                            items_matching_invoice: null,
                          }));
                        }}
                      />{" "}
                      No
                    </label>
                  </div>
                </div>

                {/* Error below */}
                {formErrors?.items_matching_invoice && (
                  <div className="mt-1">
                    <CustomError error={formErrors.items_matching_invoice} />
                  </div>
                )}
              </Col>

              {/* Anti Static Bag */}
              <Col md="6" className="mt-4">
                {/* Field row */}
                <div className="d-flex align-items-center gap-3">
                  <label className="form-label mb-0">
                    Anti-Static Bag <Asterisks asterisk={true} /> :
                  </label>

                  <div className="d-flex gap-3">
                    <label>
                      <input
                        type="radio"
                        checked={formData.inputData.anti_static_bag === true}
                        onChange={() => {
                          setFormData((p) => ({
                            ...p,
                            inputData: {
                              ...p.inputData,
                              anti_static_bag: true,
                            },
                          }));
                          setFormErrors((prev) => ({
                            ...prev,
                            anti_static_bag: null,
                          }));
                        }}
                      />{" "}
                      Yes
                    </label>

                    <label>
                      <input
                        type="radio"
                        checked={formData.inputData.anti_static_bag === false}
                        onChange={() => {
                          setFormData((p) => ({
                            ...p,
                            inputData: {
                              ...p.inputData,
                              anti_static_bag: false,
                            },
                          }));
                          setFormErrors((prev) => ({
                            ...prev,
                            anti_static_bag: null,
                          }));
                        }}
                      />{" "}
                      No
                    </label>
                  </div>
                </div>

                {/* Error below */}
                {formErrors?.anti_static_bag && (
                  <div className="mt-1">
                    <CustomError error={formErrors.anti_static_bag} />
                  </div>
                )}
              </Col>

              {/* Fault Tag Attached */}

              {/* Receiving Date */}

              {/* Fault Reported from Field */}

              {/* </Row> */}

              {/* <Row className="mt-4"> */}
              <Col md="6" className="mt-4  position-relative">
                <div className="d-flex align-items-center gap-3">
                  <label className="form-label mb-0">
                    Is Repairable <Asterisks asterisk={true} /> :
                  </label>
                  <div className="d-flex gap-3">
                    <label className="mb-0 d-flex align-items-center gap-1">
                      <input
                        type="radio"
                        checked={formData?.inputData?.is_repairable === true}
                        disabled={isRepairableDisabled}
                        onChange={() => {
                          setFormData((p) => ({
                            ...p,
                            inputData: {
                              ...p.inputData,
                              is_repairable: true,
                              scrap_reason_id: null,
                              scrap_remark: "",
                            },
                          }));

                          setFormErrors((prev) => ({
                            ...prev,
                            scrap_reason_id: null,
                            scrap_remark: null,
                            is_repairable: null,
                          }));
                        }}
                      />
                      Yes
                    </label>

                    <label className="mb-0 d-flex align-items-center gap-1">
                      <input
                        type="radio"
                        disabled={isRepairableDisabled}
                        checked={formData?.inputData?.is_repairable === false}
                        onChange={() => {
                          setFormData((p) => ({
                            ...p,
                            inputData: { ...p.inputData, is_repairable: false },
                          }));

                          setFormErrors((prev) => ({
                            ...prev,
                            is_repairable: null,
                          }));
                        }}
                      />
                      No
                    </label>
                  </div>
                </div>

                {formErrors?.is_repairable && (
                  <div className="mt-1">
                    <CustomError error={formErrors?.is_repairable} />
                  </div>
                )}
              </Col>
              {/* </Row> */}
              <Col md="6" className="mt-4">
                {/* Field row */}
                <div className="d-flex align-items-center gap-3">
                  <label className="form-label mb-0">
                    Fault Tag Attached <Asterisks asterisk={true} /> :
                  </label>

                  <div className="d-flex gap-3">
                    <label>
                      <input
                        type="radio"
                        checked={formData.inputData.fault_tag_attached === true}
                        onChange={() => {
                          setFormData((p) => ({
                            ...p,
                            inputData: {
                              ...p.inputData,
                              fault_tag_attached: true,
                            },
                          }));
                          setFormErrors((prev) => ({
                            ...prev,
                            fault_tag_attached: null,
                          }));
                        }}
                      />{" "}
                      Yes
                    </label>

                    <label>
                      <input
                        type="radio"
                        checked={
                          formData.inputData.fault_tag_attached === false
                        }
                        onChange={() => {
                          setFormData((p) => ({
                            ...p,
                            inputData: {
                              ...p.inputData,
                              fault_tag_attached: false,
                            },
                          }));
                          setFormErrors((prev) => ({
                            ...prev,
                            fault_tag_attached: null,
                          }));
                        }}
                      />{" "}
                      No
                    </label>
                  </div>
                </div>

                {/* Error below */}
                {formErrors?.fault_tag_attached && (
                  <div className="mt-1">
                    <CustomError error={formErrors.fault_tag_attached} />
                  </div>
                )}
              </Col>
              </Row>

              {technicianDetails && (
                <Col md="6" className="mt-3">
                  <strong>Engineer:</strong>{" "}
                  {technicianDetails?.technician_name}
                </Col>
              )}
              {formData?.inputData?.is_repairable === false && (
                <Row className="mt-4">
                  <Col md="6" className="mt-3">
                    <SelectInput
                      label="Scrap Reason"
                      options={scrapReasonOptions}
                      onSelect={(opt) => {
                        setFormData((prev) => ({
                          ...prev,
                          inputData: {
                            ...prev.inputData,
                            scrap_reason_id: opt?.value,
                          },
                        }));

                        setFormErrors((prev) => ({
                          ...prev,
                          scrap_reason_id: "",
                        }));
                      }}
                      placeholder="Select Scrap Reason"
                      asterisk={true}
                      customError={formErrors.scrap_reason_id}
                    />
                  </Col>
                  {formData.inputData.scrap_reason_id === 0 && (
                    <Col md="6" className="mt-3">
                      <TextAreaInput
                        label="Scrap Remark"
                        name="scrap_remark"
                        value={formData?.inputData?.scrap_remark}
                        inputClassName="form-control"
                        labelClassName="form-label"
                        onChange={onInputChange}
                        placeholder="Enter remark"
                        asterisk={true}
                        customError={formErrors.scrap_remark}
                      />
                    </Col>
                  )}
                </Row>
              )}
              <Row className="mt-4">
                <Col lg="12">
                  <CheckboxInput
                    name="is_active"
                    label="Is Active"
                    value={formData?.inputData?.is_active}
                    checked={formData?.inputData?.is_active}
                    handleTextChange={onInputChange}
                    parentClassName="form-check"
                    checkBoxClassName="form-check-input"
                    labelClassName="form-check-label"
                  />
                </Col>
              </Row>
              <div className="mt-3 d-flex gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="btn btn-primary"
                >
                  Submit
                </button>
                <button
                  type="button"
                  onClick={() => {
                    navigate(navigatePage?.back);
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </Row>
          </div>
        </div>
      )}
      {loadingData && (
        <div style={loaderOverlay}>
          <LoaderCreatePage />
        </div>
      )}
    </div>
  );
};

export default ItemIntakeCreate;
